// Private file manager: included through the existing webpack entry and Naja init.
export function initFtpManager(root = document) {
    const manager = root.matches?.('[data-ftp-manager]') ? root : root.querySelector('[data-ftp-manager]');
    if (!manager || manager.dataset.ftpReady) return;
    manager.dataset.ftpReady = '1';
    const input = manager.querySelector('[data-ftp-input]');
    const form = manager.querySelector('[data-ftp-upload]');
    const drop = manager.querySelector('[data-ftp-drop]');
    const list = manager.querySelector('[data-ftp-list]');
    const status = manager.querySelector('[data-ftp-status]');
    const progress = manager.querySelector('[data-ftp-progress]');
    const results = manager.querySelector('[data-ftp-results]');
    const transfer = manager.querySelector('[data-ftp-transfer]');
    const percentText = manager.querySelector('[data-ftp-percent]');
    const bytesText = manager.querySelector('[data-ftp-bytes]');
    const speedText = manager.querySelector('[data-ftp-speed]');
    const etaText = manager.querySelector('[data-ftp-eta]');
    const stageText = manager.querySelector('[data-ftp-stage]');
    const limitText = manager.querySelector('[data-ftp-limit]');
    const formatBytes = bytes => {
        const units = ['B', 'KiB', 'MiB', 'GiB'];
        const index = bytes > 0 ? Math.min(3, Math.floor(Math.log(bytes) / Math.log(1024))) : 0;
        return `${(bytes / 1024 ** index).toLocaleString('cs-CZ', { maximumFractionDigits: index ? 1 : 0 })} ${units[index]}`;
    };
    const duration = seconds => {
        const value = Math.max(0, Math.ceil(seconds));
        return value < 60 ? `${value} s` : `${Math.floor(value / 60)} min ${value % 60} s`;
    };
    const setText = (element, value) => { if (element) element.textContent = value; };
    let maxBytes = Number(manager.dataset.maxBytes ?? 128 * 1024 * 1024);
    const endpoint = manager.dataset.endpoint;
    const downloadFor = name => {
        const url = new URL(manager.dataset.downloadPage || `${manager.dataset.downloadBase}stahuj`, window.location.href);
        url.searchParams.set('file', name);
        return url.href;
    };
    let busy = false;
    input.multiple = true;
    manager.querySelectorAll('[data-ftp-copy]').forEach(button => button.classList.remove('hidden'));
    function lock(value) {
        busy = value;
        manager.querySelectorAll('button, input[type=file]').forEach(element => { element.disabled = value; });
        drop.setAttribute('aria-disabled', String(value));
    }
    function request(operation, payload = {}, onProgress, onUploaded) {
        return new Promise((resolve, reject) => {
            const data = new FormData();
            data.append('csrf', manager.dataset.csrf);
            data.append('operation', operation);
            Object.entries(payload).forEach(([key, value]) => data.append(key, value));
            const xhr = new XMLHttpRequest();
            const state = operation === 'state';
            const url = new URL(endpoint, window.location.href);
            if (state) url.searchParams.set('ftpState', '1');
            xhr.open(state ? 'GET' : 'POST', url.href);
            xhr.setRequestHeader('X-Ftp-Request', '1');
            if (!state) xhr.setRequestHeader('X-Ftp-Csrf', manager.dataset.csrf);
            xhr.timeout = 15 * 60 * 1000;
            if (onProgress) xhr.upload.onprogress = event => {
                if (event.lengthComputable) onProgress(event.loaded / event.total);
            };
            if (onUploaded) xhr.upload.onload = onUploaded;
            xhr.onload = () => {
                let response;
                try { response = JSON.parse(xhr.responseText); } catch {
                    reject(new Error(xhr.status === 413 ? 'Server odmítl velikost požadavku (HTTP 413). Zkontrolujte PHP a webserver limity.' : 'Server nevrátil očekávanou odpověď. Zkontrolujte limit uploadu a přihlášení.')); return;
                }
                if (xhr.status >= 200 && xhr.status < 300 && response.ok) resolve(response);
                else reject(new Error(response.error || 'Operace se nezdařila.'));
            };
            xhr.onerror = () => reject(new Error('Spojení se přerušilo. Před opakováním obnovte seznam a ověřte, zda se soubor uložil.'));
            xhr.ontimeout = () => reject(new Error('Server neodpověděl včas. Obnovte seznam a zkontrolujte soubory.'));
            xhr.send(state ? null : data);
        });
    }
    async function prepare() {
        const state = await request('state');
        if (typeof state.csrf !== 'string' || !Number.isFinite(state.maxBytes)) {
            throw new Error('Neplatná odpověď serveru. Obnovte stránku po aktualizaci aplikace.');
        }
        manager.dataset.csrf = state.csrf;
        maxBytes = state.maxBytes;
        setText(limitText, formatBytes(maxBytes));
    }
    function text(tag, value, classes) {
        const node = document.createElement(tag);
        node.textContent = value;
        if (classes) node.className = classes;
        return node;
    }
    function render(files) {
        list.replaceChildren();
        if (!files.length) {
            list.append(text('p', 'Zatím nejsou nahrané žádné soubory.', 'px-4 py-6 text-sm text-slate-500 dark:text-slate-400'));
            return;
        }
        files.forEach(file => {
            const row = text('div', '', 'flex flex-wrap items-center gap-3 border-b border-black/5 px-4 py-4 last:border-0 dark:border-white/10');
            const info = text('div', '', 'min-w-0 flex-1');
            const link = text('a', file.name, 'break-all text-sm font-medium text-slate-950 dark:text-white');
            link.href = downloadFor(file.name);
            info.append(link, text('p', `${(file.size / 1048576).toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MB · ${new Date(file.modified * 1000).toLocaleString('cs-CZ')}`, 'mt-1 text-xs text-slate-500 dark:text-slate-400'));
            const copy = text('button', 'Kopírovat odkaz', 'rounded-full bg-slate-100 px-3 py-2 text-xs font-medium dark:bg-white/10');
            copy.type = 'button'; copy.dataset.ftpCopy = downloadFor(file.name);
            const deletion = text('form', '');
            deletion.dataset.ftpDelete = ''; deletion.dataset.name = file.name;
            const remove = text('button', 'Odstranit', 'rounded-full bg-red-50 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300');
            remove.type = 'submit'; deletion.append(remove);
            row.append(info, copy, deletion); list.append(row);
        });
    }
    async function upload(files) {
        if (busy || !files.length) return;
        lock(true); results.replaceChildren();
        if (transfer) transfer.classList.remove('hidden');
        let failed = 0;
        for (const [index, file] of files.entries()) {
            progress.value = 0;
            setText(percentText, '0 %');
            setText(bytesText, `0 B / ${formatBytes(file.size)}`);
            setText(speedText, 'Rychlost: měřím…');
            setText(etaText, 'Zbývá: počítám…');
            setText(stageText, 'Ověřuji přihlášení a limit');
            status.textContent = `Nahrávám ${index + 1}/${files.length}: ${file.name}`;
            try {
                await prepare();
                if (!file.size || file.size > maxBytes) throw new Error(`Soubor má ${formatBytes(file.size)}, limit tohoto serveru je ${formatBytes(maxBytes)}. Upravte upload_max_filesize a post_max_size nebo vyberte menší soubor.`);
                setText(stageText, 'Nahrávám soubor');
                const started = performance.now();
                const update = ratio => {
                    ratio = Math.min(1, Math.max(0, ratio));
                    const bytes = file.size * ratio;
                    const elapsed = (performance.now() - started) / 1000;
                    const speed = elapsed > 0.1 ? bytes / elapsed : 0;
                    progress.value = ratio * 100;
                    setText(percentText, `${Math.floor(ratio * 100)} %`);
                    setText(bytesText, `${formatBytes(bytes)} / ${formatBytes(file.size)}`);
                    setText(speedText, speed > 0 ? `Rychlost: ${formatBytes(speed)}/s` : 'Rychlost: měřím…');
                    setText(etaText, ratio === 1 ? 'Přeneseno; čekám na server' : speed > 0 ? `Zbývá přibližně ${duration((file.size - bytes) / speed)}` : 'Zbývá: počítám…');
                };
                const response = await request('upload', { file }, update, () => {
                    update(1);
                    setText(stageText, 'Přenos dokončen, server ukládá soubor');
                });
                update(1);
                setText(stageText, 'Soubor uložen');
                setText(etaText, 'Dokončeno');
                render(response.files);
                results.append(text('li', `✓ ${file.name} — nahráno`));
            } catch (error) {
                failed++;
                setText(stageText, 'Nahrávání se nezdařilo');
                setText(etaText, 'Přenos nebyl dokončen');
                results.append(text('li', `${file.name} — ${error.message}`, 'text-red-700 dark:text-red-300'));
            }
        }
        if (!failed) form.reset();
        status.textContent = failed ? `Dokončeno. Nahráno ${files.length - failed}, chyb ${failed}.` : `Nahráno: ${files.length}. Odkazy jsou připravené.`;
        lock(false);
    }
    form.addEventListener('submit', event => {
        event.preventDefault(); event.stopPropagation(); upload(Array.from(input.files));
    });
    ['dragenter', 'dragover'].forEach(type => drop.addEventListener(type, event => {
        event.preventDefault(); if (!busy) drop.classList.add('bg-blue-50', 'dark:bg-blue-500/10');
    }));
    ['dragleave', 'drop'].forEach(type => drop.addEventListener(type, event => {
        event.preventDefault(); drop.classList.remove('bg-blue-50', 'dark:bg-blue-500/10');
    }));
    drop.addEventListener('drop', event => { upload(Array.from(event.dataTransfer.files)); });
    list.addEventListener('submit', async event => {
        const deletion = event.target.closest('[data-ftp-delete]');
        if (!deletion) return;
        event.preventDefault(); event.stopPropagation();
        if (busy || !window.confirm(`Odstranit soubor ${deletion.dataset.name}? Odkaz přestane fungovat.`)) return;
        lock(true);
        try {
            await prepare();
            const response = await request('delete', { name: deletion.dataset.name });
            render(response.files); status.textContent = 'Soubor byl odstraněn.';
        } catch (error) { status.textContent = error.message; }
        finally { lock(false); }
    });
    list.addEventListener('click', async event => {
        const button = event.target.closest('[data-ftp-copy]');
        if (!button) return;
        try {
            await navigator.clipboard.writeText(button.dataset.ftpCopy);
            status.textContent = 'Odkaz zkopírován.';
        } catch { status.textContent = `Odkaz: ${button.dataset.ftpCopy}`; }
    });
}
